// components/admin/ManualSettlement.js
import React, { useState, useEffect } from 'react';
import adminAPI from '../../services/adminAPI';
import '../../styles/admin/ManualSettlement.css';
import { useDesktopViewport } from '../../hooks/useDesktopViewport';

const ManualSettlement = () => {
    useDesktopViewport();
    const [markets, setMarkets] = useState([]);
    const [selectedMarket, setSelectedMarket] = useState(null);
    const [marketData, setMarketData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMarkets, setLoadingMarkets] = useState(true);
    const [selectedWinner, setSelectedWinner] = useState('');
    const [selectedSession, setSelectedSession] = useState(null);
    const [actualRuns, setActualRuns] = useState('');
    const [isTied, setIsTied] = useState(null);
    const [settlementResult, setSettlementResult] = useState(null);
    const [activeTab, setActiveTab] = useState('bookmaker');
    const [error, setError] = useState(null);
    const [bulkSelection, setBulkSelection] = useState(null);
    const [bulkResult, setBulkResult] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Get auth token from localStorage
    const getAuthToken = () => {
        return localStorage.getItem('token');
    };

    // Get admin ID from localStorage
    const getAdminId = () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user._id || user.id || localStorage.getItem('userId');
    };

    // Fetch all markets with unsettled bets on component mount
    useEffect(() => {
        fetchMarketsWithUnsettledBets();
    }, []);

    // Fetch markets with unsettled bets
    const fetchMarketsWithUnsettledBets = async () => {
        setLoadingMarkets(true);
        setError(null);
        try {
            const response = await adminAPI.getMarketsWithUnsettledBets();
            setMarkets(response.data || []);
        } catch (error) {
            console.error('Error fetching markets:', error);
            setError(error.response?.data?.message || 'Failed to fetch markets');
        } finally {
            setLoadingMarkets(false);
        }
    };

    // Fetch market selections for selected market
    const fetchMarketSelections = async (marketId) => {
        if (!marketId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await adminAPI.getMarketSelections(marketId);
            setMarketData(response.data);
            setSettlementResult(null);
            setSelectedWinner('');
            setSelectedSession(null);
            setActualRuns('');
            setIsTied(null);
        } catch (error) {
            console.error('Error fetching selections:', error);
            setError(error.response?.data?.message || 'Failed to fetch market data');
        } finally {
            setLoading(false);
        }
    };

    // Handle market selection
    const handleMarketSelect = (marketId) => {
        setSelectedMarket(marketId);
        fetchMarketSelections(marketId);
    };

    // Filter markets based on search
    const filteredMarkets = markets.filter(market => 
        market.marketId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (market.marketName && market.marketName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Settle bookmaker
    const settleBookmaker = async () => {
        if (!selectedWinner) {
            setError('Please select a winner');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await adminAPI.settleBookmakerByWinner({
                marketId: selectedMarket,
                winnerTeam: selectedWinner,
                betType: 'all',
                adminId: getAdminId(),
                reason: 'Manual settlement by admin'
            });
            setSettlementResult(response.results);
            alert(`✅ Settled ${response.results.settledBets} bets successfully!`);
            fetchMarketSelections(selectedMarket);
            setSelectedWinner('');
            // Refresh markets list
            fetchMarketsWithUnsettledBets();
        } catch (error) {
            console.error('Error settling:', error);
            setError(error.response?.data?.message || 'Failed to settle bets');
        } finally {
            setLoading(false);
        }
    };

    // Settle tied match
    const settleTiedMatch = async () => {
        if (isTied === null) {
            setError('Please select if match is tied or not');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await adminAPI.settleTiedMatch({
                marketId: selectedMarket,
                isTied,
                adminId: getAdminId(),
                reason: 'Manual settlement by admin'
            });
            setSettlementResult(response.results);
            alert(`✅ Settled ${response.results.settledBets} bets successfully!`);
            fetchMarketSelections(selectedMarket);
            setIsTied(null);
            fetchMarketsWithUnsettledBets();
        } catch (error) {
            console.error('Error settling:', error);
            setError(error.response?.data?.message || 'Failed to settle bets');
        } finally {
            setLoading(false);
        }
    };

    // Settle session
    const settleSession = async () => {
        if (!selectedSession) {
            setError('Please select a session bet to settle');
            return;
        }
        if (!actualRuns) {
            setError('Please enter actual runs');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await adminAPI.settleSessionByRuns({
                marketId: selectedMarket,
                selectionId: selectedSession.selectionId,
                actualRuns: parseInt(actualRuns),
                adminId: getAdminId(),
                reason: `Manual settlement - ${actualRuns} runs`
            });
            setSettlementResult(response.results);
            alert(`✅ Settled ${response.results.settledBets} bets successfully!`);
            fetchMarketSelections(selectedMarket);
            setSelectedSession(null);
            setActualRuns('');
            fetchMarketsWithUnsettledBets();
        } catch (error) {
            console.error('Error settling:', error);
            setError(error.response?.data?.message || 'Failed to settle bets');
        } finally {
            setLoading(false);
        }
    };

    // Bulk settle
    const executeBulkSettle = async (result) => {
        if (!bulkSelection) return;
        
        setLoading(true);
        setError(null);
        try {
            const response = await adminAPI.bulkSettleBySelection({
                marketId: selectedMarket,
                selectionName: bulkSelection.selectionName,
                betType: bulkSelection.betType,
                result: result,
                adminId: getAdminId(),
                reason: `Manual bulk settlement - Result: ${result.toUpperCase()}`
            });
            
            setBulkResult({
                result: result,
                ...response.results
            });
            
            alert(`✅ Settled ${response.results.settledBets} bets successfully!`);
            fetchMarketSelections(selectedMarket);
            fetchMarketsWithUnsettledBets();
            
            setTimeout(() => {
                closeBulkSettleModal();
            }, 2000);
        } catch (error) {
            console.error('Error bulk settling:', error);
            setError(error.response?.data?.message || 'Failed to settle bets');
        } finally {
            setLoading(false);
        }
    };

    const openBulkSettleModal = (selection) => {
        setBulkSelection(selection);
        setBulkResult(null);
    };

    const closeBulkSettleModal = () => {
        setBulkSelection(null);
        setBulkResult(null);
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    const getMarketIcon = (market) => {
        if (market.hasBookmaker) return '🏏';
        if (market.hasTied) return '🤝';
        if (market.hasSession) return '🎯';
        return '📋';
    };

    return (
        <div className="manual-settlement-container">
            <h2 className="h2x">⚙️ Manual Settlement Dashboard</h2>
            
            {error && (
                <div className="error-message">
                    <span>❌ {error}</span>
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {/* Market Selection Section */}
            <div className="market-selection-section">
                <h3>📋 Select Market with Unsettled Bets</h3>
                
                <div className="market-search">
                    <input
                        type="text"
                        placeholder="🔍 Search by Market ID or Name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button onClick={fetchMarketsWithUnsettledBets} disabled={loadingMarkets}>
                        🔄 Refresh
                    </button>
                </div>

                {loadingMarkets ? (
                    <div className="loading-markets">Loading markets...</div>
                ) : (
                    <div className="markets-grid">
                        {filteredMarkets.length === 0 ? (
                            <div className="no-markets">
                                <p>✅ No unsettled bets found!</p>
                                <p>All bets are settled.</p>
                            </div>
                        ) : (
                            filteredMarkets.map((market) => (
                                <div 
                                    key={market.marketId} 
                                    className={`market-card ${selectedMarket === market.marketId ? 'selected' : ''}`}
                                    onClick={() => handleMarketSelect(market.marketId)}
                                >
                                    <div className="market-header">
                                        <span className="market-icon">{getMarketIcon(market)}</span>
                                        <span className="market-id">{market.marketId}</span>
                                    </div>
                                    <div className="market-name">{market.marketName}</div>
                                    <div className="market-stats">
                                        <span className="unsettled-count">📊 {market.totalUnsettled} unsettled</span>
                                        {market.gmid && <span className="gmid">🎮 ID: {market.gmid}</span>}
                                    </div>
                                    <div className="market-badges">
                                        {market.hasBookmaker && <span className="badge bookmaker">Bookmaker</span>}
                                        {market.hasTied && <span className="badge tied">Tied</span>}
                                        {market.hasSession && <span className="badge session">Session</span>}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Settlement Section - Only show when market is selected */}
            {selectedMarket && marketData && (
                <>
                    <div className="selected-market-info">
                        <h3>📌 Currently Settling: Market {selectedMarket}</h3>
                        <button className="change-market-btn" onClick={() => {
                            setSelectedMarket(null);
                            setMarketData(null);
                            setSelectedWinner('');
                            setSelectedSession(null);
                            setActiveTab('bookmaker');
                        }}>
                            🔄 Change Market
                        </button>
                    </div>

                    <div className="settlement-tabs">
                        {marketData.bookmaker?.length > 0 && (
                            <button 
                                className={activeTab === 'bookmaker' ? 'active' : ''} 
                                onClick={() => setActiveTab('bookmaker')}
                            >
                                📋 Bookmaker ({marketData.bookmaker?.length})
                            </button>
                        )}
                        {marketData.bookmaker2?.length > 0 && (
                            <button 
                                className={activeTab === 'bookmaker2' ? 'active' : ''} 
                                onClick={() => setActiveTab('bookmaker2')}
                            >
                                📋 Bookmaker 2 ({marketData.bookmaker2?.length})
                            </button>
                        )}
                        {marketData.bookmaker_tied?.length > 0 && (
                            <button 
                                className={activeTab === 'tied' ? 'active' : ''} 
                                onClick={() => setActiveTab('tied')}
                            >
                                🤝 Tied Match ({marketData.bookmaker_tied?.length})
                            </button>
                        )}
                        {marketData.session?.length > 0 && (
                            <button 
                                className={activeTab === 'session' ? 'active' : ''} 
                                onClick={() => setActiveTab('session')}
                            >
                                🎯 Session ({marketData.session?.length})
                            </button>
                        )}
                    </div>

                    {/* Bookmaker Tab */}
                    {(activeTab === 'bookmaker' || activeTab === 'bookmaker2') && (
                        <div className="bookmaker-settlement">
                            <h3>🏏 {activeTab === 'bookmaker' ? 'Bookmaker' : 'Bookmaker 2'} Settlement</h3>
                            
                            <div className="selections-grid">
                                {marketData[activeTab]?.map((selection, idx) => (
                                    <div key={idx} className="selection-card">
                                        <div className="selection-header">
                                            <strong>{selection.selectionName}</strong>
                                            <span className={`bet-type ${selection.betType}`}>
                                                {selection.betType.toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="selection-stats">
                                            <div>📊 Bets: {selection.count}</div>
                                            <div>💰 Stake: {formatCurrency(selection.totalStake)}</div>
                                            <div>⚠️ Exposure: {formatCurrency(selection.totalExposure)}</div>
                                            <div>🎯 Payout: {formatCurrency(selection.potentialPayout)}</div>
                                            <div>📈 Odds: {selection.odds}</div>
                                        </div>
                                        <div className="selection-actions">
                                            <button 
                                                className="btn-bulk"
                                                onClick={() => openBulkSettleModal(selection)}
                                                disabled={loading}
                                            >
                                                🔧 Bulk Settle
                                            </button>
                                            <button 
                                                className="btn-winner"
                                                onClick={() => setSelectedWinner(selection.selectionName)}
                                                disabled={loading}
                                            >
                                                🏆 Set as Winner
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selectedWinner && (
                                <div className="winner-confirmation">
                                    <div className="confirmation-content">
                                        <p>🏆 Confirm Winner: <strong>{selectedWinner}</strong></p>
                                        <div className="confirmation-buttons">
                                            <button onClick={settleBookmaker} disabled={loading}>
                                                {loading ? 'Processing...' : '✅ Confirm Settlement'}
                                            </button>
                                            <button onClick={() => setSelectedWinner('')} disabled={loading}>
                                                ❌ Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tied Match Tab */}
                    {activeTab === 'tied' && (
                        <div className="tied-settlement">
                            <h3>🤝 Tied Match Settlement</h3>
                            
                            <div className="tied-options">
                                <label className={isTied === true ? 'selected' : ''}>
                                    <input 
                                        type="radio" 
                                        name="tied"
                                        checked={isTied === true}
                                        onChange={() => setIsTied(true)}
                                    />
                                    ✅ Match is TIED
                                </label>
                                <label className={isTied === false ? 'selected' : ''}>
                                    <input 
                                        type="radio" 
                                        name="tied"
                                        checked={isTied === false}
                                        onChange={() => setIsTied(false)}
                                    />
                                    ❌ Match is NOT TIED (Clear Winner)
                                </label>
                            </div>

                            <button className="btn-settle" onClick={settleTiedMatch} disabled={loading}>
                                {loading ? '⏳ Processing...' : '🎯 Settle Tied Match Bets'}
                            </button>
                        </div>
                    )}

                    {/* Session Tab */}
                    {activeTab === 'session' && (
                        <div className="session-settlement">
                            <h3>🎯 Session Settlement</h3>
                            
                            <div className="sessions-list">
                                {marketData.session?.map((session, idx) => {
                                    const isWicketBet = session.selectionName?.toLowerCase().includes('fall of') || 
                                                       session.selectionName?.toLowerCase().includes('wkt');
                                    return (
                                        <div key={idx} className="session-card">
                                            <div className="session-header">
                                                <strong>{session.selectionName}</strong>
                                                <span className="session-id">ID: {session.selectionId}</span>
                                            </div>
                                            <div className="session-badge">
                                                <span className={`bet-category ${isWicketBet ? 'wicket' : 'over'}`}>
                                                    {isWicketBet ? '🎯 Wicket Bet' : '🏏 Over Bet'}
                                                </span>
                                            </div>
                                            <div className="session-details">
                                                <div>🎯 Target: {isWicketBet ? `Wicket at` : `Over`} {session.targetValue}</div>
                                                <div>📈 Odds: {session.odds}</div>
                                                <div>⚖️ Comparison: {session.comparison === 'above' ? '⬆️ Above' : '⬇️ Below'}</div>
                                                <div>📊 Bets: {session.count}</div>
                                                <div>💰 Stake: {formatCurrency(session.totalStake)}</div>
                                                <div>⚠️ Exposure: {formatCurrency(session.totalExposure)}</div>
                                            </div>
                                            <button 
                                                className="settle-btn"
                                                onClick={() => setSelectedSession(session)}
                                                disabled={loading}
                                            >
                                                🎯 Settle This Session
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {selectedSession && (
                                <div className="runs-input-modal">
                                    <div className="modal-content">
                                        <h4>🎯 Settle Session Bet</h4>
                                        <div className="session-info">
                                            <p><strong>Selection:</strong> {selectedSession.selectionName}</p>
                                            <p><strong>Selection ID:</strong> {selectedSession.selectionId}</p>
                                            <p><strong>Target:</strong> {selectedSession.targetValue}</p>
                                            <p><strong>Odds:</strong> {selectedSession.odds}</p>
                                            <p><strong>Comparison:</strong> {selectedSession.comparison}</p>
                                            <p><strong>Total Bets:</strong> {selectedSession.count}</p>
                                        </div>
                                        <input
                                            type="number"
                                            placeholder="Enter actual runs"
                                            value={actualRuns}
                                            onChange={(e) => setActualRuns(e.target.value)}
                                            autoFocus
                                        />
                                        <div className="modal-buttons">
                                            <button onClick={settleSession} disabled={loading}>
                                                {loading ? 'Processing...' : `✅ Settle with ${actualRuns || '?'} runs`}
                                            </button>
                                            <button onClick={() => {
                                                setSelectedSession(null);
                                                setActualRuns('');
                                            }}>
                                                ❌ Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Bulk Settlement Modal */}
                    {bulkSelection && (
                        <div className="bulk-modal">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h3>🔧 Bulk Settlement</h3>
                                    <button className="close" onClick={closeBulkSettleModal}>×</button>
                                </div>
                                <div className="modal-body">
                                    <p><strong>Selection:</strong> {bulkSelection.selectionName}</p>
                                    <p><strong>Bet Type:</strong> {bulkSelection.betType.toUpperCase()}</p>
                                    <p><strong>Total Bets:</strong> {bulkSelection.count}</p>
                                    <p><strong>Total Stake:</strong> {formatCurrency(bulkSelection.totalStake)}</p>
                                    <p><strong>Total Exposure:</strong> {formatCurrency(bulkSelection.totalExposure)}</p>
                                    <p><strong>Potential Payout:</strong> {formatCurrency(bulkSelection.potentialPayout)}</p>
                                    
                                    {bulkResult && (
                                        <div className="bulk-result">
                                            <h4>Settlement Result</h4>
                                            <p>Result: <strong className={bulkResult.result === 'win' ? 'win-text' : 'loss-text'}>
                                                {bulkResult.result?.toUpperCase()}
                                            </strong></p>
                                            <p>Settled: {bulkResult.settledBets}/{bulkResult.totalBets}</p>
                                            <p>Payout: {formatCurrency(bulkResult.totalPayout)}</p>
                                            <p>Loss: {formatCurrency(bulkResult.totalLoss)}</p>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        className="btn-win"
                                        onClick={() => executeBulkSettle('win')}
                                        disabled={loading}
                                    >
                                        🎉 Mark All as WIN
                                    </button>
                                    <button 
                                        className="btn-loss"
                                        onClick={() => executeBulkSettle('loss')}
                                        disabled={loading}
                                    >
                                        💸 Mark All as LOSS
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Settlement Summary */}
                    {settlementResult && (
                        <div className="settlement-summary">
                            <h3>📊 Settlement Summary</h3>
                            <div className="summary-grid">
                                <div className="summary-item">
                                    <label>Total Bets:</label>
                                    <span>{settlementResult.totalBets}</span>
                                </div>
                                <div className="summary-item">
                                    <label>Settled:</label>
                                    <span className="success">{settlementResult.settledBets}</span>
                                </div>
                                <div className="summary-item">
                                    <label>Failed:</label>
                                    <span className="error">{settlementResult.failedBets}</span>
                                </div>
                                <div className="summary-item">
                                    <label>Total Payout:</label>
                                    <span className="success">{formatCurrency(settlementResult.totalPayout)}</span>
                                </div>
                                <div className="summary-item">
                                    <label>Total Loss:</label>
                                    <span className="error">{formatCurrency(settlementResult.totalLoss)}</span>
                                </div>
                            </div>
                            <button className="clear-btn" onClick={() => setSettlementResult(null)}>
                                Clear Summary
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ManualSettlement;